import { useEffect, useMemo, useState } from 'react';
import { useMailOrderStore, type SelectedUser } from '../../../../../stores/useMailOrderStore';
import { getSessionUser, type SessionUser } from '../../../../../utils/session';

const BLOCKED_CLASSES = [
  'BLOCKED_BY_PATIENT',
  'PATIENT_NEEDS_ACCOUNT',
  'NEEDS_PATIENT_AUTHORIZATION',
];

interface EnrollUser {
  linkToken: string;
  displayName: string;
  dob: string;
}

function buildEnrollUsers(user: SessionUser | null): EnrollUser[] {
  if (!user) return [];

  const primary: EnrollUser = {
    linkToken: (user.LinkToken as string) || '',
    displayName:
      `${((user.FirstName as string) || '').toUpperCase()} ${((user.LastName as string) || '').toUpperCase()}`.trim(),
    dob: (user.DateOfBirth as string) || '',
  };

  type Dep = {
    DependantClass?: string;
    FirstName?: string;
    LastName?: string;
    LinkToken?: string;
    DateOfBirth?: string;
    CanRefillScripts?: boolean;
  };

  const dependents = (Array.isArray(user.Dependants) ? user.Dependants : []) as Dep[];
  const eligible = dependents
    .filter(d => {
      if (!d.DependantClass || BLOCKED_CLASSES.includes(d.DependantClass)) return false;
      if (d.CanRefillScripts === false) return false;
      return true;
    })
    .map(
      (d): EnrollUser => ({
        linkToken: d.LinkToken || '',
        displayName:
          `${(d.FirstName || '').toUpperCase()} ${(d.LastName || '').toUpperCase()}`.trim(),
        dob: d.DateOfBirth || '',
      })
    );

  return [primary, ...eligible];
}

export function WizardStep1UserSelect() {
  const { selectedUser, setSelectedUser } = useMailOrderStore();
  const sessionUser = useMemo(() => getSessionUser<SessionUser>(), []);
  const enrollUsers = useMemo(() => buildEnrollUsers(sessionUser), [sessionUser]);

  const [selectedLinkToken, setSelectedLinkToken] = useState<string>(
    selectedUser?.linkToken ?? enrollUsers[0]?.linkToken ?? ''
  );

  // Sync the default pre-selection to the store on mount.
  // The radio UI pre-selects enrollUsers[0], but the store starts with selectedUser = null,
  // which causes useIsStepValid to return false and Next to stay disabled.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional empty deps — runs once on mount to sync default pre-selection to store
  useEffect(() => {
    if (!selectedUser && enrollUsers.length > 0) {
      setSelectedUser(enrollUsers[0]);
    }
  }, []);

  const { resetWizard } = useMailOrderStore();

  const handleChange = (user: EnrollUser) => {
    // If the user switches members, reset all downstream state (health, payment,
    // demographics) so no stale PHI or card data from the previous member persists.
    if (user.linkToken !== selectedLinkToken) {
      resetWizard();
      setSelectedUser(user as SelectedUser);
    }
    setSelectedLinkToken(user.linkToken);
  };

  return (
    <div>
      <h4 className="mb-3">Select Member</h4>
      <p className="text-muted mb-4">
        Select the member you would like to enroll in home delivery.
      </p>
      {enrollUsers.length === 0 ? (
        <div className="alert alert-warning">No eligible members found.</div>
      ) : (
        <div className="list-group mb-3">
          {enrollUsers.map(u => (
            <label
              key={u.linkToken}
              className={`list-group-item list-group-item-action d-flex align-items-center gap-2${
                selectedLinkToken === u.linkToken ? ' active' : ''
              }`}
            >
              <input
                type="radio"
                name="enrollUser"
                value={u.linkToken}
                checked={selectedLinkToken === u.linkToken}
                onChange={() => handleChange(u)}
                className="form-check-input mt-0"
              />
              <span style={{ marginLeft: '5px' }}>{u.displayName}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
